import { BrowserRouter as Router, Routes, Route, Navigate } from "react-router-dom";

// Existing LIVE project imports
import AdminLogin from "./components/Login/AdminLogin";
import PropertyList from "./components/PropertyList/PropertyList";
import PropertyDetails from "./components/PropertyList/PropertyDetails";
import PrivateRoute from "./components/PrivateRoute";
import HomePage from "./components/HomePage/HomePage";
import FillDgf from "./components/FillDGF/FillDgf";
import SettingsPage from "./components/Setting/SettingsPage";
import PropertyAgreement from "./components/PropertyAgreement/PropertyAgreement";
import { useEffect } from "react";
// New module imports
import RevenueForecastCalc from "./components/Frontend/revenueForecastCalc";
import HomePageRrc from "./components/Home Page Rrc/homePageRrc";
import PropertyRevenue from "./components/Property Revenue List/propertyRevenue";
import ViewTargets from "./components/View Targets/viewTargets";
import PromotionsAdmin from "./components/Promotions/PromotionsAdmin";

import Dashboard from "./components/Dashboard/Dashboard";

import useInactivityLogout from "./useInactivityLogout";

const ROLES = {
  SUPER_ADMIN: "SUPER ADMIN",
  ADMIN: "ADMIN",
  OTA_MANAGER: "OTA MANAGER",
  OTA_EXECUTIVE: "OTA EXECUTIVE",
  SALES_EXECUTIVE: "SALES EXECUTIVE",
  ONBOARDING_MANAGER: "ONBOARDING MANAGER",
  ONBOARDING_EXECUTIVE: "ONBOARDING EXECUTIVE",
};

const ALL_ROLES = [
  ROLES.SUPER_ADMIN,
  ROLES.ADMIN,
  ROLES.OTA_MANAGER,
  ROLES.OTA_EXECUTIVE,
  ROLES.SALES_EXECUTIVE,
  ROLES.ONBOARDING_MANAGER,
  ROLES.ONBOARDING_EXECUTIVE,
];

const FILL_DGF_ROLES = [
  ROLES.SUPER_ADMIN,
  ROLES.ADMIN,
  ROLES.ONBOARDING_MANAGER,
  ROLES.ONBOARDING_EXECUTIVE,
];

const PROPERTY_LIST_ROLES = [
  ROLES.SUPER_ADMIN,
  ROLES.ADMIN,
  ROLES.OTA_MANAGER,
  ROLES.OTA_EXECUTIVE,
  ROLES.SALES_EXECUTIVE,
  ROLES.ONBOARDING_MANAGER,
  ROLES.ONBOARDING_EXECUTIVE,
];

const SETTINGS_ROLES = [ROLES.SUPER_ADMIN, ROLES.ADMIN];

const RRC_HOME_ROLES = [
  ROLES.SUPER_ADMIN,
  ROLES.ADMIN,
  ROLES.OTA_MANAGER,
  ROLES.OTA_EXECUTIVE,
  ROLES.SALES_EXECUTIVE,
];

const REVENUE_CALCULATOR_ROLES = [ROLES.SUPER_ADMIN, ROLES.ADMIN, ROLES.OTA_MANAGER];

const PROPERTY_REVENUE_ROLES = [
  ROLES.SUPER_ADMIN,
  ROLES.ADMIN,
  ROLES.OTA_MANAGER,
  ROLES.OTA_EXECUTIVE,
  ROLES.SALES_EXECUTIVE,
];

const DASHBOARD_ROLES = [
  ROLES.SUPER_ADMIN,
  ROLES.ADMIN,
  ROLES.OTA_MANAGER,
  ROLES.OTA_EXECUTIVE,
  ROLES.SALES_EXECUTIVE,
];

// Same roles that see the "Promotions" card on the home page (its internal key is TOOL_5). The page also asks for the
// promotions admin password, which the SERVER checks on every request, so this list only decides who gets to the screen.
const PROMOTIONS_ROLES = [
  ROLES.SUPER_ADMIN,
  ROLES.ADMIN,
  ROLES.OTA_MANAGER,
  ROLES.OTA_EXECUTIVE,
  ROLES.SALES_EXECUTIVE,
];

// Small wrapper so the inactivity hook (which uses useNavigate() internally)
// runs INSIDE the Router context, rather than in App itself, which is the
// component that CREATES the Router and isn't yet a descendant of it.
// Renders nothing - it only exists to run the hook globally, on every page.
function InactivityWatcher() {
  useInactivityLogout();
  return null;
}

function App() {
  // FIXED: dark mode previously only ever got APPLIED to <body> while
  // SettingsPage happened to be mounted (its own useEffect was the only
  // place writing document.body.className). Loading any other page
  // fresh — or navigating away from Settings — left the saved
  // localStorage preference completely unapplied, even though it was
  // still correctly saved. This runs once at the true app root,
  // regardless of which route is active, so the class is always
  // correct from the very first paint onward. SettingsPage's own
  // toggle still works exactly as before — it just changes the same
  // class this now also sets on load.
  useEffect(() => {
    const savedTheme = localStorage.getItem("theme") || "light";
    document.body.className = savedTheme === "dark" ? "dark-theme" : "light-theme";
  }, []);

  return (
    <Router>
      <InactivityWatcher />
      <Routes>
        <Route path="/admin" element={<AdminLogin />} />
        <Route path="/" element={<Navigate to="/admin" replace />} />

        <Route
          path="/HomePage"
          element={
            <PrivateRoute allowedRoles={ALL_ROLES}>
              <HomePage />
            </PrivateRoute>
          }
        />

        <Route
          path="/PropertiesList"
          element={
            <PrivateRoute
              allowedRoles={PROPERTY_LIST_ROLES}
              requiredPermissions={["read_properties_list", "read_write_properties_list"]}
            >
              <PropertyList />
            </PrivateRoute>
          }
        />

        <Route
          path="/property-details"
          element={
            <PrivateRoute
              allowedRoles={PROPERTY_LIST_ROLES}
              requiredPermissions={["read_properties_list", "read_write_properties_list"]}
            >
              <PropertyDetails />
            </PrivateRoute>
          }
        />

        <Route
          path="/FillDgf"
          element={
            <PrivateRoute
              allowedRoles={FILL_DGF_ROLES}
              requiredPermissions={["read_fill_dgf", "read_write_fill_dgf"]}
            >
              <FillDgf />
            </PrivateRoute>
          }
        />

        <Route
          path="/PropertyAgreement"
          element={
            <PrivateRoute allowedRoles={FILL_DGF_ROLES}>
              <PropertyAgreement />
            </PrivateRoute>
          }
        />

        <Route
          path="/SettingsPage"
          element={
            <PrivateRoute allowedRoles={SETTINGS_ROLES}>
              <SettingsPage />
            </PrivateRoute>
          }
        />

        <Route
          path="/rfc"
          element={
            <PrivateRoute
              allowedRoles={REVENUE_CALCULATOR_ROLES}
              requiredPermissions={["read_targets_write_revenue_calc"]}
            >
              <RevenueForecastCalc />
            </PrivateRoute>
          }
        />

        <Route
          path="/HomePageRrc"
          element={
            <PrivateRoute
              allowedRoles={RRC_HOME_ROLES}
              requiredPermissions={["show_rrc", "show_rrc_revenue_calculator", "show_rrc_property_wise_targets"]}
            >
              <HomePageRrc />
            </PrivateRoute>
          }
        />

        <Route
          path="/homepagerrc"
          element={
            <PrivateRoute
              allowedRoles={RRC_HOME_ROLES}
              requiredPermissions={["show_rrc", "show_rrc_revenue_calculator", "show_rrc_property_wise_targets"]}
            >
              <HomePageRrc />
            </PrivateRoute>
          }
        />

        <Route
          path="/propertyrevenue"
          element={
            <PrivateRoute
              allowedRoles={PROPERTY_REVENUE_ROLES}
              requiredPermissions={["read_property_wise_targets", "read_targets_write_revenue_calc"]}
            >
              <PropertyRevenue />
            </PrivateRoute>
          }
        />

        <Route
          path="/view-targets/:id"
          element={
            <PrivateRoute
              allowedRoles={PROPERTY_REVENUE_ROLES}
              requiredPermissions={["read_property_wise_targets", "read_targets_write_revenue_calc"]}
            >
              <ViewTargets />
            </PrivateRoute>
          }
        />

        <Route
          path="/dashboards"
          element={
            <PrivateRoute
              allowedRoles={DASHBOARD_ROLES}
              requiredPermissions={["show_dashboards", "read_dashboards"]}
            >
              <Dashboard />
            </PrivateRoute>
          }
        />

        <Route
          path="/promotions"
          element={
            <PrivateRoute allowedRoles={PROMOTIONS_ROLES}>
              <PromotionsAdmin />
            </PrivateRoute>
          }
        />

        <Route path="*" element={<Navigate to="/admin" replace />} />
      </Routes>
    </Router>
  );
}

export default App;
